import { LoadingSpinner } from "components/LoadingSpinner";
import {
  ICelebrationOrigin,
  WorklistCelebration,
} from "pages/EligibilityPage/WorklistCelebration";
import { Button } from "primereact/button";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import {
  useGetMyTrainingQuery,
  useGetQuizQuery,
  useSubmitQuizMutation,
} from "store/queries/training";
import { userSelector } from "store/slices/user";
import styled from "styled-components";
import { trackTraining } from "training/engine/telemetry";
import {
  CERTIFICATE_PATH,
  keepRightAnswers,
  toAnswers,
  whereItLives,
} from "training/quizHelpers";
import { IQuizGrade, IQuizQuestionResult } from "types/Training";

const Page = styled.div`
  max-width: 760px;
  margin: 0 auto;
  padding: 24px 0 48px;
`;

/** Same voice as the tour's popover header: purple eyebrow, title, one-sentence purpose. */
const Header = styled.header`
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--brand-gray-200);
`;

const Eyebrow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--brand-purple-700);
  font-size: 0.8rem;
  font-weight: var(--font-weight-semibold);
`;

const Title = styled.h1`
  margin: 4px 0 6px;
  font-size: 1.5rem;
  font-weight: var(--font-weight-semibold);
`;

const Purpose = styled.p`
  margin: 0;
  color: var(--brand-gray-700);
`;

const Question = styled.fieldset`
  margin: 0 0 16px;
  padding: 16px;
  border: 1px solid var(--brand-gray-200);
  border-radius: 12px;

  &[data-result="wrong"] {
    border-color: var(--red-300, #fca5a5);
    background: var(--red-50, #fef2f2);
  }

  &[data-result="right"] {
    border-color: var(--green-300, #86efac);
  }
`;

const Legend = styled.legend`
  float: left;
  width: 100%;
  margin-bottom: 10px;
  padding: 0;
  font-weight: var(--font-weight-semibold);
`;

const Choice = styled.label`
  clear: left;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 6px 4px;
  border-radius: 6px;
  cursor: pointer;

  input {
    margin-top: 3px;
    accent-color: var(--brand-purple-700);
  }

  &[data-disabled="true"] {
    cursor: default;
  }
`;

const Feedback = styled.div`
  margin-top: 10px;
  font-size: 0.875rem;

  p {
    margin: 0 0 4px;
  }
`;

const Footer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
`;

const Score = styled.p`
  margin: 0;
  font-weight: var(--font-weight-semibold);
`;

const Success = styled.section`
  margin: 0 0 20px;
  padding: 20px;
  border: 2px solid var(--brand-purple-700);
  border-radius: 12px;
  background: var(--brand-blue-25);
  text-align: center;

  .pi-verified {
    color: var(--brand-purple-700);
    font-size: 2.5rem;
  }

  h2 {
    margin: 8px 0 4px;
    color: var(--brand-purple-700);
  }

  p {
    margin: 0 0 12px;
  }
`;

const ErrorText = styled.p`
  margin: 0;
  color: var(--red-600, #dc2626);
`;

function ResultFeedback({
  result,
  choices,
}: {
  result: IQuizQuestionResult;
  choices: string[];
}) {
  if (result.correct) return null;
  const where = whereItLives(result.stepId);
  return (
    <Feedback data-testid={`quiz-feedback-${result.questionId}`}>
      <p>
        <strong>Right answer:</strong> {choices[result.correctIndex]}
      </p>
      {where ? (
        <p>
          <strong>Where it lives:</strong> Module {where.module},{" "}
          {where.moduleTitle} — {where.what}
        </p>
      ) : null}
    </Feedback>
  );
}

/**
 * CLE-3015 — /training/quiz, the knowledge check (spec §2: twelve questions, pass = all twelve, retries
 * allowed). All twelve on one page; Submit waits until every question has an answer. The API grades (it
 * is the only place the answers live) and returns, for each wrong one, the right choice and the
 * curriculum step it "lives" at, which this page names. Try again keeps the right answers and clears the
 * wrong ones. A pass certifies the user on the server: confetti, then the certificate. The banner goes
 * away by itself because the submit invalidates "Training".
 *
 * HospitalStaff only (the route's WithRoleProtection sends a SuperAdmin away); a user who is already
 * certified is sent to the hospital view — but not the one who passed a moment ago on this page.
 */
export function QuizPage() {
  const navigate = useNavigate();
  const { currentAccount } = useSelector(userSelector);
  const { data: trainingData, isLoading: trainingLoading } =
    useGetMyTrainingQuery(currentAccount);
  const {
    data: quizData,
    isLoading: quizLoading,
    isError: quizError,
  } = useGetQuizQuery();
  const [submitQuiz, { isLoading: submitting }] = useSubmitQuizMutation();

  const [selected, setSelected] = useState<Record<string, number>>({});
  const [grade, setGrade] = useState<IQuizGrade | null>(null);
  const [submitFailed, setSubmitFailed] = useState(false);
  const [celebrate, setCelebrate] = useState<ICelebrationOrigin | null>(null);
  const passedHere = useRef(false);
  const submitRef = useRef<HTMLDivElement>(null);
  const startedTracked = useRef(false);

  const record = trainingData?.data;
  const view = quizData?.data;
  const questions = useMemo(() => view?.questions ?? [], [view]);
  const resultsById = useMemo(
    () =>
      new Map((grade?.results ?? []).map((r) => [r.questionId, r] as const)),
    [grade]
  );
  const telemetryBase = {
    accountId: currentAccount ?? "",
    curriculumVersion: record?.curriculumVersion ?? 1,
  };

  useEffect(() => {
    if (startedTracked.current || !questions.length || !record) return;
    if (record.clearCertifiedAt) return;
    startedTracked.current = true;
    trackTraining("training_quiz_started", {
      ...telemetryBase,
      attempts: view?.quiz?.attempts ?? 0,
    });
  }, [questions.length, record]);

  if (trainingLoading || quizLoading) return <LoadingSpinner />;

  const hospitalView = currentAccount
    ? `/hospitals/view/${currentAccount}/orders`
    : "/";
  if (record?.clearCertifiedAt && !passedHere.current) {
    return <Navigate to={hospitalView} replace />;
  }

  const allAnswered =
    questions.length > 0 &&
    questions.every((q) => selected[q.id] !== undefined);
  const graded = !!grade;
  const passed = !!grade?.passed;

  const handleSubmit = async () => {
    setSubmitFailed(false);
    try {
      const response = await submitQuiz(toAnswers(selected)).unwrap();
      const result = response.data;
      if (result.passed) passedHere.current = true;
      setGrade(result);
      trackTraining("training_quiz_submitted", {
        ...telemetryBase,
        score: result.score,
        attempts: result.attempts,
      });
      if (result.passed) {
        trackTraining("training_certified", {
          ...telemetryBase,
          score: result.score,
          attempts: result.attempts,
        });
        const rect = submitRef.current?.getBoundingClientRect();
        setCelebrate(
          rect
            ? { x: rect.left + rect.width / 2, y: rect.top }
            : { x: window.innerWidth / 2, y: window.innerHeight / 2 }
        );
      }
      window.scrollTo?.({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error(
        "[Clear Certified] submitting the knowledge check failed",
        error
      );
      setSubmitFailed(true);
    }
  };

  const handleTryAgain = () => {
    const wrong = (grade?.results ?? [])
      .filter((r) => !r.correct)
      .map((r) => r.questionId);
    setSelected((current) => keepRightAnswers(current, wrong));
    setGrade(null);
  };

  return (
    <Page>
      {celebrate ? (
        <WorklistCelebration
          origin={celebrate}
          onDone={() => setCelebrate(null)}
        />
      ) : null}
      <Header>
        <Eyebrow>
          <i className="pi pi-verified" aria-hidden />
          Get Clear Certified · Knowledge check
        </Eyebrow>
        <Title>The knowledge check</Title>
        <Purpose>
          Twelve quick questions. Get one wrong and we show you the answer and
          where it lives. Answer all twelve right to become Clear Certified; you
          can try as many times as you like.
        </Purpose>
      </Header>

      {passed ? (
        <Success data-testid="quiz-success">
          <i className="pi pi-verified" aria-hidden />
          <h2>You are Clear Certified</h2>
          <p>
            All twelve right. Your certificate is ready to view, print or
            download.
          </p>
          <Button
            type="button"
            label="View your certificate"
            onClick={() => navigate(CERTIFICATE_PATH)}
          />
        </Success>
      ) : null}

      {graded && !passed ? (
        <Score role="status" style={{ marginBottom: 16 }}>
          You got {grade?.score} of {grade?.total}. The ones to fix are marked
          below.
        </Score>
      ) : null}

      {quizError ? (
        <ErrorText role="alert">
          The knowledge check could not be loaded. Reload the page to try again.
        </ErrorText>
      ) : null}

      {questions.map((question, index) => {
        const result = resultsById.get(question.id);
        let outcome: "right" | "wrong" | undefined;
        if (result) outcome = result.correct ? "right" : "wrong";
        return (
          <Question
            key={question.id}
            data-testid={`quiz-question-${question.id}`}
            data-result={outcome}
          >
            <Legend>
              {index + 1}. {question.question}
            </Legend>
            {question.choices.map((choice, choiceIndex) => (
              <Choice
                // The API's order is stable; the index is the answer.
                // eslint-disable-next-line react/no-array-index-key
                key={choiceIndex}
                data-disabled={graded}
              >
                <input
                  type="radio"
                  name={question.id}
                  value={choiceIndex}
                  checked={selected[question.id] === choiceIndex}
                  disabled={graded}
                  onChange={() =>
                    setSelected((current) => ({
                      ...current,
                      [question.id]: choiceIndex,
                    }))
                  }
                />
                <span>{choice}</span>
              </Choice>
            ))}
            {result ? (
              <ResultFeedback result={result} choices={question.choices} />
            ) : null}
          </Question>
        );
      })}

      {questions.length ? (
        <Footer ref={submitRef}>
          {graded && !passed ? (
            <>
              <Score>
                You got {grade?.score} of {grade?.total}.
              </Score>
              <Button
                type="button"
                label="Try again"
                icon="pi pi-refresh"
                onClick={handleTryAgain}
              />
            </>
          ) : null}
          {!graded ? (
            <Button
              type="button"
              label="Submit"
              disabled={!allAnswered || submitting}
              loading={submitting}
              onClick={handleSubmit}
            />
          ) : null}
          {!graded && !allAnswered ? (
            <span>Answer every question to submit.</span>
          ) : null}
          {submitFailed ? (
            <ErrorText role="alert">
              Your answers could not be sent. Check your connection and press
              Submit again.
            </ErrorText>
          ) : null}
        </Footer>
      ) : null}
    </Page>
  );
}
